import React from "react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import { Link } from "react-router-dom";

export function PrivacyPage() {
  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-16 max-w-4xl" testId="privacy-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ PRIVACY POLICY</div>
        <h1 className="font-heading text-4xl font-black">Privacy Policy</h1>
        <div className="mt-8 space-y-6 text-[color:var(--dt6-muted)] leading-relaxed">
          <p><strong className="text-[color:var(--dt6-text)]">Effective date:</strong> {new Date().getFullYear()}-01-01</p>
          <p>
            DT6 ("we", "us") provides political data infrastructure to America-First campaigns, PACs, party committees, consultants,
            and movement organizations. This policy describes what we collect, why, and your rights.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Data we collect</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Account information: name, email, organization, role.</li>
            <li>Client-provided datasets: voter files, contact lists, and related metadata submitted for processing.</li>
            <li>Operational logs: request metadata, audit events, IP address for security and rate-limiting.</li>
            <li>Marketing intake: name, email, and free-text message when you contact us or request an audit.</li>
          </ul>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">How we use it</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide the six-module stack (source, clean, identify, score, route, activate).</li>
            <li>To respond to inbound requests, contact forms, and DSARs.</li>
            <li>To meet legal, TCPA, CAN-SPAM, and state-privacy obligations.</li>
            <li>To improve the platform in aggregate — never to sell your data outside the DT6 client relationship.</li>
          </ul>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Your rights</h3>
          <p>
            Depending on your state of residence, you may have the right to access, delete, correct, or opt-out
            of the processing of your personal information. Submit a request via <Link to="/dsar" className="dt6-link">/dsar</Link>.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">IE / CE separation</h3>
          <p>
            Independent-expenditure and coordinated-expenditure data are logically separated. Access is role-gated and every
            operation is written to an immutable audit log.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Contact</h3>
          <p>
            Send questions to privacy@dt6.com. We respond to verified requests within 30 days per applicable law.
          </p>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}

export function TermsPage() {
  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-16 max-w-4xl" testId="terms-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ TERMS OF SERVICE</div>
        <h1 className="font-heading text-4xl font-black">Terms of Service</h1>
        <div className="mt-8 space-y-6 text-[color:var(--dt6-muted)] leading-relaxed">
          <p><strong className="text-[color:var(--dt6-text)]">Effective date:</strong> {new Date().getFullYear()}-01-01</p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Acceptable use</h3>
          <p>
            DT6 is licensed to America-First campaigns, PACs, committees, consultants, and movement organizations. DT6 reserves the right
            to refuse or terminate service to any party whose use conflicts with the platform's mission or applicable law.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Client data</h3>
          <p>
            Client-provided data remains the property of the client. DT6 processes such data under a limited license to deliver the
            contracted services. Data is retained per contract and deleted upon request or at contract end.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Compliance</h3>
          <p>
            Clients are responsible for their own compliance with TCPA, CAN-SPAM, state privacy laws, and applicable FEC and state
            election commission rules. DT6 provides posture and tooling; the licensed operator is the responsible party.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Warranties and limitation of liability</h3>
          <p>
            DT6 is provided "as is." To the fullest extent permitted by law, DT6's liability is limited to fees paid in the trailing
            12 months. Nothing in these terms limits liability that cannot be limited by law.
          </p>
          <h3 className="font-heading text-xl text-[color:var(--dt6-text)]">Governing law</h3>
          <p>These terms are governed by the laws of the State of Delaware, United States.</p>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}

import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export function DsarPage() {
  const [form, setForm] = useState({
    full_name: "", email: "", request_type: "access", state: "", details: "",
  });
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/intake/dsar", form);
      setOk(true);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-16 max-w-3xl" testId="dsar-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ DATA-SUBJECT REQUEST</div>
        <h1 className="font-heading text-4xl font-black">Request your data.</h1>
        <p className="mt-4 text-[color:var(--dt6-muted)]">
          Under applicable state privacy laws, you may request access to, deletion of, or correction of your personal data.
          We respond to verified requests within 30 days.
        </p>

        {ok ? (
          <div className="dt6-empty mt-8" data-testid="dsar-success">
            <CheckCircle2 size={24} strokeWidth={1.5} className="text-[color:var(--dt6-success)] mx-auto mb-3" />
            REQUEST RECEIVED. WE WILL CONTACT YOU WITHIN 30 DAYS.
          </div>
        ) : (
          <form onSubmit={submit} className="dt6-card p-8 mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dsar-form">
            <div className="md:col-span-2">
              <label className="dt6-label">Full name</label>
              <input className="dt6-input" required value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="dsar-name" />
            </div>
            <div>
              <label className="dt6-label">Email</label>
              <input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="dsar-email" />
            </div>
            <div>
              <label className="dt6-label">State of residence</label>
              <input className="dt6-input" value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} data-testid="dsar-state" />
            </div>
            <div className="md:col-span-2">
              <label className="dt6-label">Request type</label>
              <select className="dt6-select" value={form.request_type} onChange={(e)=>setForm({...form,request_type:e.target.value})} data-testid="dsar-type">
                <option value="access">Access</option>
                <option value="delete">Delete</option>
                <option value="correct">Correct</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="dt6-label">Details</label>
              <textarea className="dt6-textarea" rows={5} value={form.details} onChange={(e)=>setForm({...form,details:e.target.value})} data-testid="dsar-details" />
            </div>
            {err && <div className="md:col-span-2 text-sm text-[color:var(--dt6-danger)]" data-testid="dsar-error">{err}</div>}
            <div className="md:col-span-2 flex justify-end">
              <button className="dt6-btn dt6-btn-primary" disabled={busy} data-testid="dsar-submit">
                {busy ? "SUBMITTING..." : "Submit request"}
              </button>
            </div>
          </form>
        )}
      </Section>
      <PublicFooter />
    </>
  );
}
